import type { FC, ReactNode } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import FilterListOffRoundedIcon from '@mui/icons-material/FilterListOffRounded';
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import ErrorOverlayWithRefetchButton from 'src/components/dashboard/DashboardPage/components/ErrorOverlayWithRefetchButton';

import type { WidgetExportOptions } from './useWidgetExport';
import useWidgetExport from './useWidgetExport';

import { styles } from './index.styles';

export interface WidgetFrameProps {
  children: ReactNode;
  header: ReactNode;
  footer?: ReactNode;
  error?: string;
  loading?: boolean;
  refetch: () => void;
  minHeight?: string;
  maxHeight?: string;
  ariaId?: string;
  notAffectedBy?: 'filters' | 'comparison';
  exportOptions?: WidgetExportOptions;
  downloadDisabled?: boolean;
}

const WIDGET_HEIGHT = '37.125rem';

const WidgetFrame: FC<WidgetFrameProps> = ({
  children,
  footer,
  header,
  error,
  loading,
  refetch,
  minHeight = WIDGET_HEIGHT,
  maxHeight = WIDGET_HEIGHT,
  ariaId,
  notAffectedBy,
  exportOptions,
  downloadDisabled,
}) => {
  const { t } = useTranslation(['common', 'followUp']);
  const handleExport = useWidgetExport(exportOptions);

  const notEffectedByText = useMemo(() => {
    switch (notAffectedBy) {
      case 'filters': {
        return t('followUp:filtersDoNotAffectThisWidget');
      }
      case 'comparison': {
        return t('followUp:comparisonDoesNotAffectThisWidget');
      }
    }
    return '';
  }, [notAffectedBy, t]);

  return (
    <Stack
      direction="column"
      component="section"
      aria-labelledby={ariaId}
      sx={{ minHeight: minHeight, ...styles.frame }}
    >
      <Box sx={styles.header}>
        <Stack direction="row" spacing={2}>
          {loading && (
            <Box>
              <CircularProgress size={20} />
            </Box>
          )}
          <Box>{header}</Box>
        </Stack>

        <Box sx={styles.headerActions}>
          {notAffectedBy && (
            <Tooltip title={notEffectedByText}>
              <Typography variant="caption" sx={styles.icon}>
                <Box component="span" sx={styles.iconText}>
                  {notEffectedByText}
                </Box>
                <FilterListOffRoundedIcon fontSize="small" />
              </Typography>
            </Tooltip>
          )}

          {!loading && !error && !downloadDisabled && (
            <IconButton
              color="primary"
              sx={styles.downloadButton}
              aria-label={t('common:download')}
              onClick={handleExport}
            >
              <DownloadRoundedIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          flexGrow: 1,
          maxHeight: maxHeight,
          width: '100%',
        }}
      >
        {error && (
          <ErrorOverlayWithRefetchButton errorMsg={error} refetch={refetch} />
        )}

        {!error && children}
      </Stack>
      <Box sx={styles.footer}>{footer}</Box>
    </Stack>
  );
};

export default WidgetFrame;
